import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft } from 'lucide-react';

const DemoPlayground = () => {
  // States
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [profile, setProfile] = useState('BackOffice');
  const [showWelcome, setShowWelcome] = useState(true);
  const [activeMethods, setActiveMethods] = useState({
    vector: false,
    keyword: false,
    graph: false,
    metadata: false
  });
  const [sessionStats, setSessionStats] = useState({
    queries: 0,
    totalTime: 0,
    satisfactionSum: 0,
    satisfactionCount: 0,
    totalSources: 0
  });
  
  // Settings states
  const [model, setModel] = useState('gpt-4-turbo');
  const [temperature, setTemperature] = useState(0.7);
  const [cacheEnabled, setCacheEnabled] = useState(true);
  const [sourcesEnabled, setSourcesEnabled] = useState(true);
  
  // Refs
  const chatInputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-resize textarea
  const handleInputChange = (e) => {
    const textarea = e.target;
    setInputValue(textarea.value);
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Set example question
  const setQuestion = (question) => {
    setInputValue(question);
    if (chatInputRef.current) {
      chatInputRef.current.focus();
      chatInputRef.current.style.height = 'auto';
      chatInputRef.current.style.height = Math.min(chatInputRef.current.scrollHeight, 120) + 'px';
    }
  };

  // Update profile
  const updateProfile = (e) => {
    const newProfile = e.target.value;
    setProfile(newProfile);
    addSystemMessage(`Profil changé vers: ${newProfile}`);
  };

  // Add system message
  const addSystemMessage = (text) => {
    const systemMessage = {
      id: Date.now(),
      type: 'system',
      text: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, systemMessage]);
  };

  // Rate satisfaction
  const rateSatisfaction = (messageId, rating) => {
    setSessionStats(prev => ({
      ...prev,
      satisfactionSum: prev.satisfactionSum + rating,
      satisfactionCount: prev.satisfactionCount + 1
    }));
    
    // Update message to show rating
    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, rated: rating }
        : msg
    ));
  };

  // Simulate search methods activation
  const simulateSearchMethods = async () => {
    const methods = ['vector', 'keyword', 'graph', 'metadata'];
    
    // Reset all methods
    setActiveMethods({
      vector: false,
      keyword: false,
      graph: false,
      metadata: false
    });
    
    // Activate methods sequentially
    for (let i = 0; i < methods.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300));
      setActiveMethods(prev => ({
        ...prev,
        [methods[i]]: true
      }));
    }
  };

  // Generate response (simulated)
  const generateResponse = async (question) => {
    const processingTime = cacheEnabled ? 800 + Math.random() * 400 : 1200 + Math.random() * 800;
    await new Promise(resolve => setTimeout(resolve, processingTime));
    
    // Sample responses based on question content
    const responses = {
      'chèque impayé': {
        answer: `Pour traiter un chèque impayé selon les règles BEAC, voici la procédure à suivre :\n\n1. **Vérification du motif** : Identifier la cause du rejet (provision insuffisante, compte fermé, etc.)\n2. **Application des frais** : Appliquer les frais selon le barème BEAC en vigueur\n3. **Notification client** : Informer le client sous 48h ouvrées\n4. **Enregistrement** : Saisir l'incident dans le fichier central des impayés\n\n*Cette procédure est adaptée au profil ${profile}.*`,
        confidence: 95.2,
        sources: [
          { title: 'Procédure BEAC n°2023-15', score: 98, type: 'regulation' },
          { title: 'Module gub_cheque_reject.4gl', score: 89, type: 'code' },
          { title: 'Guide BackOffice - Section 7', score: 84, type: 'manual' }
        ]
      },
      'ORA-12154': {
        answer: `L'erreur ORA-12154 "TNS:could not resolve the connect identifier" indique un problème de configuration Oracle :\n\n**Diagnostic :**\n• Vérifier le fichier tnsnames.ora\n• Contrôler la variable ORACLE_HOME\n• Tester la connectivité réseau\n\n**Solution :**\n\`\`\`bash\n# Vérifier la configuration\ntnsping PAYDB\n# Tester la connexion\nsqlplus user/pass@PAYDB\n\`\`\`\n\n*Réponse optimisée pour le profil ${profile}.*`,
        confidence: 92.7,
        sources: [
          { title: 'Oracle Error Reference', score: 96, type: 'documentation' },
          { title: 'Module db_connection.4gl', score: 88, type: 'code' },
          { title: 'Troubleshooting Guide', score: 79, type: 'manual' }
        ]
      },
      'microservices': {
        answer: `Architecture microservices recommandée pour l'API SWIFT :\n\n**Services proposés :**\n• **Gateway Service** : Routage et authentification\n• **Message Parser** : Traitement des messages MT\n• **Validation Service** : Contrôles réglementaires\n• **Persistence Service** : Stockage transactionnel\n\n**Technologies :**\n• Spring Boot + Docker\n• Apache Kafka pour messaging\n• PostgreSQL pour persistence\n• Redis pour cache\n\n*Architecture adaptée aux contraintes ${profile}.*`,
        confidence: 89.5,
        sources: [
          { title: 'SWIFT API Guidelines', score: 94, type: 'specification' },
          { title: 'Microservices Patterns', score: 87, type: 'architecture' },
          { title: 'Banking API Standards', score: 82, type: 'regulation' }
        ]
      },
      'directive BEAC': {
        answer: `Impact de la nouvelle directive BEAC 2024 sur nos processus :\n\n**Changements majeurs :**\n• Nouveaux seuils de déclaration (>50M FCFA)\n• Procédures KYC renforcées\n• Délais de traitement réduits (24h max)\n\n**Actions requises :**\n1. Mise à jour des modules de contrôle\n2. Formation des équipes BackOffice\n3. Adaptation des rapports réglementaires\n\n**Timeline :** Implémentation avant le 31/03/2025\n\n*Analyse détaillée pour le profil ${profile}.*`,
        confidence: 87.3,
        sources: [
          { title: 'Directive BEAC 2024-08', score: 97, type: 'regulation' },
          { title: 'Impact Analysis Report', score: 91, type: 'analysis' },
          { title: 'Compliance Checklist', score: 85, type: 'procedure' }
        ]
      }
    };
    
    // Find best matching response
    let bestResponse = {
      answer: `Je comprends votre question sur "${question}". Basé sur mon analyse des documents disponibles et votre profil ${profile}, voici les informations pertinentes :\n\nCette question nécessite une recherche approfondie dans notre base de connaissances. Les 4 méthodes de recherche hybride ont été utilisées pour vous fournir la réponse la plus précise possible.\n\n*Réponse générée par ${model} avec le profil ${profile}.*`,
      confidence: 78.5,
      sources: [
        { title: 'Base de connaissances générale', score: 78, type: 'general' },
        { title: 'Procédures standards', score: 72, type: 'procedure' }
      ]
    };
    
    // Check for keyword matches
    Object.keys(responses).forEach(keyword => {
      if (question.toLowerCase().includes(keyword.toLowerCase())) {
        bestResponse = responses[keyword];
      }
    });
    
    return bestResponse;
  };

  // Send message
  const sendMessage = async () => {
    const input = inputValue.trim();
    if (!input || isSending) return;
    
    // Clear welcome message
    setShowWelcome(false);
    
    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    
    // Clear input and set sending state
    setInputValue('');
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
    }
    setIsSending(true);
    setIsTyping(true);
    
    // Simulate search methods activation
    await simulateSearchMethods();
    
    // Generate response
    const startTime = Date.now();
    const response = await generateResponse(input);
    const endTime = Date.now();
    const responseTime = endTime - startTime;
    
    // Add assistant message
    const assistantMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      text: response.answer,
      confidence: response.confidence,
      sources: response.sources,
      responseTime: responseTime,
      timestamp: new Date()
    };
    
    setIsTyping(false);
    setMessages(prev => [...prev, assistantMessage]);
    
    // Update stats
    setSessionStats(prev => ({
      queries: prev.queries + 1,
      totalTime: prev.totalTime + responseTime,
      satisfactionSum: prev.satisfactionSum,
      satisfactionCount: prev.satisfactionCount,
      totalSources: prev.totalSources + response.sources.length
    }));
    
    // Reset states
    setIsSending(false);
    setActiveMethods({
      vector: false,
      keyword: false,
      graph: false,
      metadata: false
    });
    
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  };

  // Scroll to bottom when new messages
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize demo
  useEffect(() => {
    const timer = setTimeout(() => {
      addSystemMessage('💡 Astuce: Utilisez les exemples de questions dans la sidebar pour commencer');
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
            overflow-x: hidden;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 80px 2rem 2rem;
            min-height: 100vh;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 3rem;
        }
        
        .page-title {
            font-size: 3rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.2rem;
            color: #888;
        }
        
        /* Demo Layout */
        .demo-layout {
            display: grid;
            grid-template-columns: 1fr 400px;
            gap: 2rem;
            min-height: calc(100vh - 200px);
        }
        
        /* Chat Interface */
        .chat-container {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 20px;
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        
        .chat-header {
            background: rgba(102, 126, 234, 0.1);
            padding: 1.5rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .chat-title {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: bold;
        }
        
        .status-indicator {
            width: 8px;
            height: 8px;
            background: #67e8a9;
            border-radius: 50%;
            animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.3); }
        }
        
        .profile-selector {
            padding: 0.5rem 1rem;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 6px;
            color: white;
            font-size: 0.9rem;
        }
        
        .chat-messages {
            flex: 1;
            padding: 1.5rem;
            overflow-y: auto;
            max-height: 60vh;
        }
        
        .message {
            margin-bottom: 1.5rem;
            animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        .message-user {
            text-align: right;
        }
        
        .message-bubble {
            display: inline-block;
            max-width: 80%;
            padding: 1rem 1.5rem;
            border-radius: 18px;
            margin-bottom: 0.5rem;
        }
        
        .user-bubble {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }
        
        .assistant-bubble {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            color: #e0e0e0;
            white-space: pre-wrap;
        }
        
        .message-meta {
            font-size: 0.8rem;
            color: #888;
            margin-top: 0.5rem;
        }
        
        .sources-section {
            margin-top: 1rem;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.03);
            border-radius: 8px;
            border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .sources-title {
            font-size: 0.9rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
            color: #667eea;
        }
        
        .source-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        .source-item:last-child {
            border-bottom: none;
        }
        
        .source-title {
            flex: 1;
            font-size: 0.9rem;
            color: #ccc;
        }
        
        .source-score {
            font-size: 0.8rem;
            font-weight: bold;
            color: #67e8a9;
        }
        
        .source-type {
            font-size: 0.7rem;
            color: #888;
            background: rgba(255, 255, 255, 0.1);
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            margin-left: 0.5rem;
        }
        
        .typing-indicator {
            padding: 1rem 1.5rem;
            color: #888;
            font-style: italic;
        }
        
        .typing-dots {
            display: inline-block;
        }
        
        .typing-dots span {
            display: inline-block;
            width: 4px;
            height: 4px;
            background: #888;
            border-radius: 50%;
            margin: 0 1px;
            animation: typing 1.4s ease-in-out infinite;
        }
        
        .typing-dots span:nth-child(2) {
            animation-delay: 0.2s;
        }
        
        .typing-dots span:nth-child(3) {
            animation-delay: 0.4s;
        }
        
        @keyframes typing {
            0%, 60%, 100% { transform: translateY(0); }
            30% { transform: translateY(-10px); }
        }
        
        .chat-input-area {
            padding: 1.5rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(255, 255, 255, 0.02);
        }
        
        .input-container {
            display: flex;
            gap: 1rem;
            align-items: flex-end;
        }
        
        .chat-input {
            flex: 1;
            min-height: 50px;
            max-height: 120px;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.05);
            border: 2px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: white;
            font-size: 1rem;
            resize: none;
            transition: all 0.3s;
        }
        
        .chat-input:focus {
            outline: none;
            border-color: rgba(102, 126, 234, 0.5);
            background: rgba(255, 255, 255, 0.08);
        }
        
        .chat-input::placeholder {
            color: #666;
        }
        
        .send-button {
            width: 50px;
            height: 50px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 12px;
            color: white;
            font-size: 1.2rem;
            cursor: pointer;
            transition: all 0.3s;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .send-button:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
        }
        
        .send-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
        }
        
        /* Sidebar */
        .sidebar {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }
        
        /* Example Queries */
        .examples-section {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
        }
        
        .section-title {
            font-size: 1.1rem;
            font-weight: bold;
            margin-bottom: 1rem;
            color: #667eea;
        }
        
        .example-query {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 0.8rem;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .example-query:hover {
            background: rgba(102, 126, 234, 0.1);
            border-color: rgba(102, 126, 234, 0.3);
            transform: translateX(5px);
        }
        
        .example-text {
            font-size: 0.9rem;
            margin-bottom: 0.5rem;
        }
        
        .example-meta {
            font-size: 0.8rem;
            color: #888;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .example-profile {
            background: rgba(102, 126, 234, 0.2);
            padding: 0.2rem 0.5rem;
            border-radius: 4px;
            color: #667eea;
        }
        
        /* Stats Panel */
        .stats-panel {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
        }
        
        .stats-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
        }
        
        .stat-item {
            text-align: center;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
        }
        
        .stat-value {
            font-size: 1.5rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .stat-label {
            font-size: 0.8rem;
            color: #888;
            margin-top: 0.3rem;
        }
        
        /* Method Visualization */
        .methods-panel {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
        }
        
        .method-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.8rem;
            margin-bottom: 0.5rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
            transition: all 0.3s;
        }
        
        .method-item.active {
            background: rgba(102, 126, 234, 0.2);
            border: 1px solid rgba(102, 126, 234, 0.3);
        }
        
        .method-name {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 600;
        }
        
        .method-icon {
            font-size: 1.2rem;
        }
        
        .method-weight {
            font-size: 1.1rem;
            font-weight: bold;
            color: #667eea;
        }
        
        .method-status {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #888;
            transition: all 0.3s;
        }
        
        .method-status.active {
            background: #67e8a9;
            animation: pulse 1s ease-in-out infinite;
        }
        
        /* Settings Panel */
        .settings-panel {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
        }
        
        .setting-item {
            margin-bottom: 1rem;
        }
        
        .setting-label {
            font-size: 0.9rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: #ccc;
        }
        
        .setting-input {
            width: 100%;
            padding: 0.5rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            color: white;
            font-size: 0.9rem;
        }
        
        .setting-checkbox {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
        }
        
        .setting-checkbox input {
            width: 16px;
            height: 16px;
        }
        
        /* Welcome Message */
        .welcome-message {
            text-align: center;
            padding: 2rem;
            color: #888;
        }
        
        .welcome-title {
            font-size: 1.2rem;
            font-weight: bold;
            margin-bottom: 0.5rem;
            color: #667eea;
        }
        
        .welcome-text {
            font-size: 0.9rem;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
            .demo-layout {
                grid-template-columns: 1fr;
            }
            
            .sidebar {
                order: -1;
            }
        }
        
        @media (max-width: 768px) {
            .stats-grid {
                grid-template-columns: 1fr;
            }
            
            .input-container {
                flex-direction: column;
                gap: 0.5rem;
            }
            
            .send-button {
                width: 100%;
                height: 45px;
            }
        }
        
        .system-message {
            text-align: center;
            color: #667eea;
            font-style: italic;
            font-size: 0.9rem;
        }
        
        .rating-button {
            background: none;
            border: none;
            color: #888;
            cursor: pointer;
            font-size: 1.2rem;
            transition: all 0.3s;
        }
        
        .rating-button.rated-positive {
            color: #67e8a9;
            transform: scale(1.2);
        }
        
        .rating-button.rated-negative {
            color: #f56565;
            transform: scale(1.2);
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Démo Interactive</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Playground Interactif</h1>
          <p className="page-subtitle">Testez l'Agent RAG Hybride Banking en temps réel</p>
        </div>
        
        {/* Demo Layout */}
        <div className="demo-layout">
          {/* Chat Interface */}
          <div className="chat-container">
            <div className="chat-header">
              <div className="chat-title">
                <span className="status-indicator"></span>
                Agent RAG Hybride
              </div>
              <select 
                className="profile-selector" 
                value={profile}
                onChange={updateProfile}
              >
                <option value="BackOffice">👥 BackOffice</option>
                <option value="IT">💻 IT</option>
                <option value="AMOA">📊 AMOA</option>
                <option value="MOE">🏗️ MOE</option>
              </select>
            </div>
            
            <div className="chat-messages" ref={messagesContainerRef}>
              {showWelcome && (
                <div className="welcome-message">
                  <div className="welcome-title">🚀 Bienvenue dans le Playground RAG Hybride</div>
                  <div className="welcome-text">
                    Posez vos questions bancaires et découvrez comment notre agent combine 4 méthodes de recherche pour vous donner les meilleures réponses.
                  </div>
                </div>
              )}
              
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.type === 'user' ? 'message-user' : ''}`}>
                  {message.type === 'system' ? (
                    <div className="system-message">{message.text}</div>
                  ) : message.type === 'user' ? (
                    <>
                      <div className="message-bubble user-bubble">{message.text}</div>
                      <div className="message-meta">{message.timestamp.toLocaleTimeString()}</div>
                    </>
                  ) : (
                    <>
                      <div className="message-bubble assistant-bubble">
                        {message.text}
                        {sourcesEnabled && message.sources && message.sources.length > 0 && (
                          <div className="sources-section">
                            <div className="sources-title">📚 Sources consultées</div>
                            {message.sources.map((source, idx) => (
                              <div key={idx} className="source-item">
                                <div className="source-title">{source.title}</div>
                                <div className="source-score">{source.score}%</div>
                                <div className="source-type">{source.type}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="message-meta">
                        {message.timestamp.toLocaleTimeString()} • 
                        {message.responseTime}ms • 
                        Confiance: {message.confidence}%
                        <span style={{marginLeft: '1rem'}}>
                          <button 
                            onClick={() => rateSatisfaction(message.id, 1)} 
                            className={`rating-button ${message.rated === 1 ? 'rated-positive' : ''}`}
                          >
                            👍
                          </button>
                          <button 
                            onClick={() => rateSatisfaction(message.id, 0)} 
                            className={`rating-button ${message.rated === 0 ? 'rated-negative' : ''}`}
                          >
                            👎
                          </button>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
              
              {isTyping && (
                <div className="typing-indicator">
                  L'agent analyse votre question<span className="typing-dots"><span></span><span></span><span></span></span>
                </div>
              )}
            </div>
            
            <div className="chat-input-area">
              <div className="input-container">
                <textarea 
                  ref={chatInputRef}
                  className="chat-input" 
                  placeholder="Posez votre question sur les procédures bancaires..."
                  rows="1"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyPress}
                />
                <button 
                  className="send-button" 
                  onClick={sendMessage}
                  disabled={isSending || !inputValue.trim()}
                >
                  ➤
                </button>
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="sidebar">
            {/* Example Queries */}
            <div className="examples-section">
              <h3 className="section-title">💡 Exemples de Questions</h3>
              
              <div className="example-query" onClick={() => setQuestion('Comment traiter un chèque impayé selon BEAC?')}>
                <div className="example-text">Comment traiter un chèque impayé selon BEAC?</div>
                <div className="example-meta">
                  <span>Procédure</span>
                  <span className="example-profile">BackOffice</span>
                </div>
              </div>
              
              <div className="example-query" onClick={() => setQuestion('Erreur ORA-12154 dans le module de paiement')}>
                <div className="example-text">Erreur ORA-12154 dans le module de paiement</div>
                <div className="example-meta">
                  <span>Debug</span>
                  <span className="example-profile">IT</span>
                </div>
              </div>
              
              <div className="example-query" onClick={() => setQuestion('Architecture microservices pour nouvelle API SWIFT')}>
                <div className="example-text">Architecture microservices pour nouvelle API SWIFT</div>
                <div className="example-meta">
                  <span>Architecture</span>
                  <span className="example-profile">MOE</span>
                </div>
              </div>
              
              <div className="example-query" onClick={() => setQuestion('Impact nouvelle directive BEAC 2024 sur nos processus')}>
                <div className="example-text">Impact nouvelle directive BEAC 2024 sur nos processus</div>
                <div className="example-meta">
                  <span>Analyse</span>
                  <span className="example-profile">AMOA</span>
                </div>
              </div>
            </div>
            
            {/* Search Methods Visualization */}
            <div className="methods-panel">
              <h3 className="section-title">🔍 Méthodes de Recherche</h3>
              
              <div className={`method-item ${activeMethods.vector ? 'active' : ''}`}>
                <div className="method-name">
                  <span className="method-icon">🧠</span>
                  <span>Vectorielle</span>
                </div>
                <div className="method-weight">60%</div>
                <div className={`method-status ${activeMethods.vector ? 'active' : ''}`}></div>
              </div>
              
              <div className={`method-item ${activeMethods.keyword ? 'active' : ''}`}>
                <div className="method-name">
                  <span className="method-icon">🔍</span>
                  <span>Keywords</span>
                </div>
                <div className="method-weight">25%</div>
                <div className={`method-status ${activeMethods.keyword ? 'active' : ''}`}></div>
              </div>
              
              <div className={`method-item ${activeMethods.graph ? 'active' : ''}`}>
                <div className="method-name">
                  <span className="method-icon">🕸️</span>
                  <span>Graphe</span>
                </div>
                <div className="method-weight">10%</div>
                <div className={`method-status ${activeMethods.graph ? 'active' : ''}`}></div>
              </div>
              
              <div className={`method-item ${activeMethods.metadata ? 'active' : ''}`}>
                <div className="method-name">
                  <span className="method-icon">📋</span>
                  <span>Metadata</span>
                </div>
                <div className="method-weight">5%</div>
                <div className={`method-status ${activeMethods.metadata ? 'active' : ''}`}></div>
              </div>
            </div>
            
            {/* Stats Panel */}
            <div className="stats-panel">
              <h3 className="section-title">📊 Statistiques Session</h3>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{sessionStats.queries}</div>
                  <div className="stat-label">Questions</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {sessionStats.queries > 0 
                      ? Math.round(sessionStats.totalTime / sessionStats.queries) + 'ms' 
                      : '-'}
                  </div>
                  <div className="stat-label">Temps Moy.</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {sessionStats.satisfactionCount > 0 
                      ? Math.round((sessionStats.satisfactionSum / sessionStats.satisfactionCount) * 100) + '%' 
                      : '-'}
                  </div>
                  <div className="stat-label">Satisfaction</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{sessionStats.totalSources}</div>
                  <div className="stat-label">Sources</div>
                </div>
              </div>
            </div>
            
            {/* Settings Panel */}
            <div className="settings-panel">
              <h3 className="section-title">⚙️ Configuration</h3>
              
              <div className="setting-item">
                <div className="setting-label">Modèle LLM</div>
                <select 
                  className="setting-input" 
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                >
                  <option value="gpt-4-turbo">GPT-4 Turbo</option>
                  <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                  <option value="mixtral-8x7b">Mixtral 8x7B</option>
                </select>
              </div>
              
              <div className="setting-item">
                <div className="setting-label">Température</div>
                <input 
                  type="range" 
                  className="setting-input" 
                  min="0" 
                  max="1" 
                  step="0.1" 
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                />
                <div style={{textAlign: 'center', color: '#667eea', fontSize: '0.8rem', marginTop: '0.3rem'}}>
                  <span>{temperature}</span>
                </div>
              </div>
              
              <div className="setting-item">
                <label className="setting-checkbox">
                  <input 
                    type="checkbox" 
                    checked={cacheEnabled}
                    onChange={(e) => setCacheEnabled(e.target.checked)}
                  />
                  <span>Utiliser le cache</span>
                </label>
              </div>
              
              <div className="setting-item">
                <label className="setting-checkbox">
                  <input 
                    type="checkbox" 
                    checked={sourcesEnabled}
                    onChange={(e) => setSourcesEnabled(e.target.checked)}
                  />
                  <span>Afficher les sources</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoPlayground;